import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ColoredDot, Flex } from 'styling/GlobalStyles'
import {
  BedContainer,
  BedFooter,
  BedHistoryBedCampContainer,
  BedMiddleSection,
  BlockStatusesContainer,
  CampContainer,
  CampOverviewContainer,
  ColoredSquare,
  SectionIconContainer,
} from '../Accommodation.style'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import Chevron from 'assets/icons/Chevron'
import { PersonIcon } from '../icons/PersonIcon'
import { AppDispatch, RootState } from 'store'
import { clearBeds, getBeds } from 'features/accommodation/accommodationSlice'
import { formatParams } from 'utils/helpers'
import { IGetAllBedsSingleResponse } from 'types/api/accommodationApi.interface'
import { BedIcon } from '../icons/BedIcon'
import { LocationIconOrange } from '../icons/LocationIconOrange'
import { NoData } from 'components/NoData'

const colors = {
  totalCapacity: '#ff9800',
  paleOrange: '#FF99001A',
  available: '#10B981',
  paleGreen: '#24D07B1A',
  occupied: '#51A2FF',
  paleBlue: '#00A6FF26',
  dirty: '#F4D03F',
  paleYellow: '#FF99001A',
  blocked: '#FF6467',
  paleRed: '#E74C3C1A',

}
const BED_STATE_MAP: Record<number, { label: string; color: string; iconBackground?: string; icon?: JSX.Element }> = {
  1: { label: 'AVAILABLE', color: colors.available, iconBackground: colors.paleGreen, icon: <BedIcon color={colors.available} /> },
  2: { label: 'OCCUPIED', color: colors.occupied, iconBackground: colors.paleBlue, icon: <BedIcon /> },
  3: { label: 'DIRTY', color: colors.dirty, iconBackground: colors.paleYellow, icon: <BedIcon /> },
  4: { label: 'BLOCKED', color: colors.blocked, iconBackground: colors.paleRed, icon: <BedIcon /> },
}

const getBedStateInfo = (bedState: number) =>
  BED_STATE_MAP[bedState] ?? { label: 'UNKNOWN', color: colors.blocked }

const groupBedsByCamp = (
  beds: IGetAllBedsSingleResponse[],
): Record<string, IGetAllBedsSingleResponse[]> => {
  return beds.reduce(
    (acc, bed) => {
      const campName = bed.campName || 'Unassigned'
      if (!acc[campName]) acc[campName] = []
      acc[campName].push(bed)
      return acc
    },
    {} as Record<string, IGetAllBedsSingleResponse[]>,
  )
}

const BedItem = ({ bed }: { bed: IGetAllBedsSingleResponse }) => {
  const stateInfo = getBedStateInfo(bed.bedState)
  return (
    <BedContainer onClick={(e) => e.stopPropagation()} style={{ border: '1px solid' + stateInfo.color }} >
      <Flex gap="0.5rem" width="100%" between mb='12' >
        <Flex gap="0.5rem">
          <SectionIconContainer color={stateInfo.iconBackground} >
            {stateInfo.icon}
          </SectionIconContainer>
          <Flex gap='1rem' center>
            <TitleText weight='bold' >{bed.label}</TitleText>
            <ColoredDot color={stateInfo.color} />
            <BodyText color={stateInfo.color}>{stateInfo.label}</BodyText>
          </Flex>
        </Flex>
      </Flex>

      <BedMiddleSection borderColor={stateInfo.color}>
        <LocationIconOrange />
        <OtherText color='#ff9900' >{bed.roomName}</OtherText>
        <OtherText weight='bold' >{bed.blockName.toUpperCase()}</OtherText>
      </BedMiddleSection>

      <BedFooter>
        <OtherText grey >BED POLICIES</OtherText>
        <Flex width="100%" gap='1rem'>
          <OtherText grey>Gender:</OtherText>
          <BodyText weight="bold">
            {bed.blockGenderPolicy === 1 ? 'MALE' : bed.blockGenderPolicy === 2 ? 'FEMALE' : 'ANY'}
          </BodyText>
        </Flex>
        <Flex width="100%" gap='1rem'>
          <OtherText grey>Tier:</OtherText>
          <BodyText weight="bold">
            {bed.blockTierPolicy === 1
              ? 'STANDARD'
              : bed.blockTierPolicy === 2
                ? 'PREMIUM'
                : 'ANY'}
          </BodyText>
        </Flex>
      </BedFooter>
    </BedContainer>
  )
}

const BlockSection = ({
  campName,
  campBeds,
  active,
  onToggle,
}: {
  campName: string
  campBeds: IGetAllBedsSingleResponse[]
  active: boolean
  onToggle: () => void
}) => {
  const roomsCount = new Set(campBeds.map((b) => b.roomName)).size
  const bedsCount = campBeds.length

  return (
    <CampContainer active={active} onClick={onToggle}>
      <CampOverviewContainer>
        <Flex column between height="100%">
          <BodyText style={{ fontSize: 20 }}>{campName}</BodyText>
          <BodyText grey>
            {roomsCount} rooms · {bedsCount} beds total
          </BodyText>
        </Flex>
        <Flex height="100%" gap="1rem" center>
          <Chevron open={active} />
        </Flex>
      </CampOverviewContainer>
      <BedHistoryBedCampContainer style={{ padding: '1rem' }} >
        {campBeds.map((bed) => (
          <BedItem key={bed.id} bed={bed} />
        ))}
      </BedHistoryBedCampContainer>
    </CampContainer>
  )
}

export const BedManagement = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { beds, isLoading } = useSelector((store: RootState) => store.accomodation)
  const [params, setParams] = useState({ page: 1, limit: 20 })
  const [selectedCamp, setSelectedCamp] = useState<{ value: number; label: string } | null>(null)
  const [openCamp, setOpenCamp] = useState<string | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const isInitialMount = useRef(true)

  const bedsGroupedByCamp = useMemo(
    () => groupBedsByCamp(beds.data),
    [beds.data],
  )

  const hasMore = params.page < beds.totalPages
  const campEntries = Object.entries(bedsGroupedByCamp)

  useEffect(() => {
    const fetchParams = {
      ...params,
      ...(selectedCamp?.value && { campId: selectedCamp.value }),
    }
    dispatch(getBeds(formatParams(fetchParams)))
  }, [dispatch, params.page, params.limit, selectedCamp?.value])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    dispatch(clearBeds())
    setParams((p) => ({ ...p, page: 1 }))
  }, [selectedCamp?.value, dispatch])

  const lastPostElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || !hasMore) return
      if (observerRef.current) observerRef.current.disconnect()

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          setParams((prev) => ({ ...prev, page: prev.page + 1 }))
        }
      })

      if (node) observerRef.current.observe(node)
    },
    [isLoading, hasMore],
  )

  return (
    <>
      {/* <Box
        width="100%"
        height="105px"
        style={{
          padding: '1rem',
          borderTop: '1px solid #ff9900',
          borderRadius: '0.5rem',
        }}
      >
         <HonestSelectInput
          name="camp"
          labelText="Select Camp"
          options={[]}
          onChange={(v) =>
            setSelectedCamp(
              Array.isArray(v) ? null : (v as { value: number; label: string } | null),
            )
          }
          width="335px"
        /> 
      </Box> */}
      <Flex column gap="1rem" mt="16">
        {campEntries.length ? campEntries.map(([campName, campBeds]) => (
          <BlockSection key={campName} campName={campName} campBeds={campBeds} active={openCamp === campName} onToggle={() => setOpenCamp(prev => prev === campName ? null : campName)} />
        )) : <NoData />}
        {hasMore && (
          <div ref={lastPostElementRef} style={{ height: 20, width: '100%' }}>
            {isLoading && <BodyText grey>Loading more...</BodyText>}
          </div>
        )}
      </Flex>
    </>
  )
}
